import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BarChart } from '@/widgets/BarChart'
import { ActivityFeed } from '@/widgets/ActivityFeed'
import { dashboardsService } from '@/services/dashboardsService'
import { HRDashboardData } from '@/api/types'
import { config } from '@/core/config'
import { PageHeader } from '@/components/shared/PageHeader'

/**
 * HR Dashboard Page
 * Displays HR KPIs, department distribution, and recent activities
 */
export const HRDashboard: React.FC = () => {
  const { t } = useTranslation('common')
  const [data, setData] = useState<HRDashboardData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true)
        
        let dashboardData: HRDashboardData
        
        if (config.features.useMockData) {
          dashboardData = dashboardsService.getMockHRDashboard()
        } else {
          dashboardData = await dashboardsService.getHRDashboard(1)
        }
        
        setData(dashboardData)
      } catch (error) {
        console.error('Failed to load HR dashboard:', error)
        setData(dashboardsService.getMockHRDashboard())
      } finally {
        setLoading(false)
      }
    }

    loadDashboardData()
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('dashboard.hr.title')} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('dashboard.hr.title')} />
        <div className="text-center py-12">
          <p className="text-muted-foreground">{t('common.error')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-black min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {t('hr.dashboardTitle')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('hr.dashboardSubtitle')}
        </p>
      </div>

      {/* KPI Cards - Compact Professional Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-border p-4">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-muted-foreground">{t('hr.totalEmployeesLabel')}</h3>
            <div className="p-2 rounded-lg bg-gray-300 dark:bg-gray-600">
              <svg className="w-4 h-4 text-black dark:text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-2xl font-bold">{data.stats.totalEmployees.current}</div>
            <p className="text-xs text-muted-foreground">Active employees</p>
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-border p-4">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-muted-foreground">{t('hr.newHiresLabel')}</h3>
            <div className="p-2 rounded-lg bg-gray-300 dark:bg-gray-600">
              <svg className="w-4 h-4 text-black dark:text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-2xl font-bold">{data.stats.newHires.thisMonth}</div>
            <p className="text-xs text-muted-foreground">{t('hr.newHiresThisMonth')}</p>
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-border p-4">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-muted-foreground">{t('hr.onLeaveLabel')}</h3>
            <div className="p-2 rounded-lg bg-gray-300 dark:bg-gray-600">
              <svg className="w-4 h-4 text-black dark:text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-2xl font-bold">{data.stats.onLeave.current}</div>
            <p className="text-xs text-muted-foreground">{data.stats.onLeave.scheduled} {t('hr.scheduled')}</p>
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-border p-4">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-muted-foreground">{t('hr.attritionRate')}</h3>
            <div className="p-2 rounded-lg bg-gray-300 dark:bg-gray-600">
              <svg className="w-4 h-4 text-black dark:text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-2xl font-bold">{data.stats.attritionRate.current}%</div>
            <p className="text-xs text-muted-foreground">{t('hr.target')}: {data.stats.attritionRate.target}%</p>
          </div>
        </div>
      </div>

      {/* Charts Section - Professional Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarChart
          title={t('hr.departmentDistribution')}
          description={t('hr.departmentDistributionDescription')}
          data={data.departmentChart}
          color="hsl(var(--primary))"
          className="border-border"
          height={320}
        />
        
        <ActivityFeed
          title={t('hr.recentActivity')}
          description={t('hr.recentActivityDescription')}
          activities={data.recentActivity}
          maxItems={5}
          className="border-border"
        />
      </div>
    </div>
  )
} 